import { useEffect, useState } from "react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Input } from "../../components/ui/input.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";

export type Metric =
  | "INSUFFICIENT_HOURS" | "WEEKEND_WORK" | "HOLIDAY_WORK" | "LEAVE_WORK" | "OVERTIME_APPROVAL" | "SHIFT_CHANGE" | "WEEKLY_HOURS";

export interface AlertRule {
  id: string;
  name: string;
  metric: Metric;
  thresholdKind: "HOURS" | "PERCENT";
  thresholdValue: string | number;
  graceMinutes: number;
  requiresApproval: boolean;
  isActive: boolean;
  notifyReportingManager: boolean;
  notifySrManager: boolean;
  notifyManager: boolean;
  notifyCorporateHr: boolean;
  emailReportingManager: boolean;
  emailSrManager: boolean;
  emailManager: boolean;
  emailCorporateHr: boolean;
  /** False until an admin saves the card: the server then runs its own defaults and the card shows empty. */
  configured: boolean;
}

export const METRIC_LABEL: Record<Metric, string> = {
  INSUFFICIENT_HOURS: "Insufficient hours",
  WEEKEND_WORK: "Weekend working",
  HOLIDAY_WORK: "Holiday working",
  LEAVE_WORK: "Leave-day working",
  OVERTIME_APPROVAL: "Overtime",
  SHIFT_CHANGE: "Shift change",
  WEEKLY_HOURS: "Weekly hours",
};

const METRIC_DESCRIPTION: Record<Metric, string> = {
  INSUFFICIENT_HOURS: "When someone's day falls short of the hours expected of them. Leave, holidays and weekends are never flagged.",
  WEEKEND_WORK: "When someone works any hours on a Saturday or Sunday. Until it is approved, that day shows as awaiting approval, not present.",
  HOLIDAY_WORK: "When someone checks in on a public holiday. Until it is approved, that day shows as awaiting approval, not present.",
  LEAVE_WORK: "When someone checks in on a full day of approved leave. Until it is approved, that day shows as awaiting approval, not present. Half-day leave and WFH are not affected.",
  OVERTIME_APPROVAL: "When someone works beyond the weekly limit. Checked every Monday for the week just finished, never day by day.",
  SHIFT_CHANGE: "When a manager reschedules someone, or someone asks to work different hours. Changes made by HR are never reported.",
  WEEKLY_HOURS: "When someone's week falls short of the expected hours. Checked every Monday for the week just finished — leave, holidays and weekends never count against the total.",
};

/** Wording for the threshold box, which means something different per feature. */
const THRESHOLD_LABEL: Record<Metric, string | null> = {
  INSUFFICIENT_HOURS: "Expected day",
  WEEKEND_WORK: null,
  HOLIDAY_WORK: null,
  LEAVE_WORK: null,
  OVERTIME_APPROVAL: "Weekly limit",
  SHIFT_CHANGE: null,
  WEEKLY_HOURS: "Expected week",
};

const HAS_GRACE = new Set<Metric>(["INSUFFICIENT_HOURS", "OVERTIME_APPROVAL", "WEEKLY_HOURS"]);
/** Judged after the week ends by the Monday job, not the moment someone checks out. */
const WEEKLY = (m: Metric) => m === "WEEKLY_HOURS";

const RECIPIENTS: Array<[keyof AlertRule & string, string]> = [
  ["notifyReportingManager", "Reporting Manager"],
  ["notifySrManager", "Sr Manager"],
  ["notifyManager", "HR Manager"],
  ["notifyCorporateHr", "Corporate HR"],
];
const EMAILS: Array<[keyof AlertRule & string, string]> = [
  ["emailReportingManager", "Reporting Manager"],
  ["emailSrManager", "Sr Manager"],
  ["emailManager", "HR Manager"],
  ["emailCorporateHr", "Corporate HR"],
];
/** The employee's own manager is always in the loop; the tick is shown, not offered. */
const LOCKED_ON = "notifyReportingManager";

/** Hours and grace are text-or-blank so a card nobody has set can show empty boxes. */
type Draft = Omit<AlertRule, "id" | "thresholdValue" | "graceMinutes"> & { thresholdValue: string; graceMinutes: number | "" };

function draftOf(r: AlertRule): Draft {
  const { id: _id, ...rest } = r;
  // Not set by anyone yet: show nothing the server is quietly using. The reporting manager
  // is always on; every other box, the hours and the grace start empty.
  if (!r.configured) {
    return {
      ...rest, isActive: false, thresholdValue: "", graceMinutes: "",
      notifySrManager: false, notifyManager: false, notifyCorporateHr: false,
      emailReportingManager: false, emailSrManager: false, emailManager: false, emailCorporateHr: false,
    };
  }
  return { ...rest, thresholdValue: String(r.thresholdValue), graceMinutes: r.graceMinutes ?? 0 };
}

/** "9h" / "9h 30m" — a bar in words. */
function describeHours(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function BoxRow({ fields, draft, onChange, disabled }: {
  fields: Array<[string, string]>;
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {fields.map(([k, label]) => {
        const locked = k === LOCKED_ON;
        return (
          <label
            key={k}
            className={locked ? "flex items-center gap-2 text-[13px]" : "flex cursor-pointer items-center gap-2 text-[13px]"}
            title={locked ? "The employee's own reporting manager is always included" : undefined}
          >
            <Checkbox
              checked={locked ? true : Boolean(draft[k as keyof Draft])}
              disabled={disabled || locked}
              onCheckedChange={(v) => onChange({ [k]: v === true } as Partial<Draft>)}
              aria-label={label}
            />
            {label}
            {locked && <span className="text-[11px] text-surface-500">(always)</span>}
          </label>
        );
      })}
    </div>
  );
}

/**
 * One attendance feature, configured on its own card.
 *
 * Every feature uses this same component so they cannot drift into presenting the
 * same decisions differently — which is what the old table-plus-bespoke-overtime-card
 * arrangement did. What varies per feature is declared in the maps above, not in
 * separate copies of the layout.
 */
export function FeatureCard({ rule, onSaved }: { rule: AlertRule; onSaved: () => void }) {
  const { show } = useToast();
  const confirm = useConfirm();
  const [d, setD] = useState<Draft>(() => draftOf(rule));
  useEffect(() => setD(draftOf(rule)), [rule]);

  const patch = (p: Partial<Draft>) => setD({ ...d, ...p });
  // An unset card can always be saved: saving is how an admin takes it over from the defaults,
  // including by switching it off.
  const dirty = !rule.configured || JSON.stringify(d) !== JSON.stringify(draftOf(rule));

  const metric = d.metric;
  const thresholdLabel = THRESHOLD_LABEL[metric];
  const hours = Number(d.thresholdValue);
  const hoursOk = !thresholdLabel || (Number.isFinite(hours) && hours > 0);
  // A blank grace box means no grace.
  const graceMinutes = d.graceMinutes === "" ? 0 : d.graceMinutes;
  const graceOk = Number.isInteger(graceMinutes) && graceMinutes >= 0 && graceMinutes <= 480;
  const showGrace = HAS_GRACE.has(metric);

  // A floor (insufficient day, expected week) is missed by its grace, so grace lowers the bar;
  // a ceiling (overtime) is overrun by its grace, so grace raises it.
  const grace = graceMinutes / 60;
  const floor = metric === "INSUFFICIENT_HOURS" || metric === "WEEKLY_HOURS";
  const bar = floor ? hours - grace : hours + grace;

  function barSentence() {
    if (!hoursOk || !graceOk) return null;
    if (metric === "WEEKEND_WORK") return "Any hours worked on a Saturday or Sunday.";
    if (metric === "HOLIDAY_WORK") return "Any hours worked on a public holiday.";
    if (metric === "LEAVE_WORK") return "Any hours worked on a full day of approved leave.";
    if (metric === "SHIFT_CHANGE") return "Every shift a manager changes is reported. Each request for different hours waits for the people ticked to decide.";
    if (metric === "WEEKLY_HOURS") {
      const perDay = Math.round((hours / 5) * 100) / 100;
      return `Flags a week under ${describeHours(bar)}${graceMinutes ? ` — up to ${graceMinutes} minutes short is ignored` : ""}. That is ${describeHours(perDay)} per working day; leave, holidays and weekends subtract their own share.`;
    }
    if (metric === "INSUFFICIENT_HOURS") {
      return `Flags a day under ${describeHours(bar)}${graceMinutes ? ` — being up to ${graceMinutes} minutes short is ignored` : ""}.`;
    }
    if (metric === "OVERTIME_APPROVAL") return `Flags a week over ${describeHours(bar)}${graceMinutes ? ` — an overrun up to ${graceMinutes} minutes is ignored` : ""}.`;
    return `Flags a day over ${describeHours(bar)}${graceMinutes ? ` — an overrun up to ${graceMinutes} minutes is ignored` : ""}.`;
  }

  function validate(): string | null {
    // A card being switched off has nothing to measure, so its hours may stay blank.
    if (d.isActive && thresholdLabel && !hoursOk) return "Enter a number of hours above 0";
    if (!graceOk) return "Grace must be between 0 and 480 minutes";
    // No "pick at least one" check: the reporting manager is always included.
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { show(err, "error"); throw new Error("validation"); }

    const ok = await confirm(
      d.isActive
        ? {
            title: rule.isActive ? `Apply these ${METRIC_LABEL[metric].toLowerCase()} settings?` : `Turn ${METRIC_LABEL[metric].toLowerCase()} on?`,
            description: `${barSentence()} Each request becomes one the people ticked can approve or decline.`,
            actionText: rule.isActive ? "Apply" : "Turn on",
            tone: "default",
          }
        : {
            title: `Turn ${METRIC_LABEL[metric].toLowerCase()} off?`,
            description: "Nothing will be raised for this feature. Anything already pending stays approvable.",
            actionText: "Turn off",
            tone: "warning",
          },
    );
    if (!ok) return;

    try {
      await api.patch(`/admin/attendance-alert-rules/${rule.id}`, {
        // Blank only when switching an unset card off: the stored figure stands in.
        thresholdValue: d.thresholdValue === "" ? Number(rule.thresholdValue) : Number(d.thresholdValue),
        thresholdKind: "HOURS",
        graceMinutes: showGrace ? graceMinutes : 0,
        requiresApproval: true,
        isActive: d.isActive,
        notifyRegionalHr: false,
        notifyReportingManager: true,
        notifySrManager: d.notifySrManager,
        notifyManager: d.notifyManager,
        notifyCorporateHr: d.notifyCorporateHr,
        emailReportingManager: d.emailReportingManager,
        emailSrManager: d.emailSrManager,
        emailManager: d.emailManager,
        emailCorporateHr: d.emailCorporateHr,
      });
      show(`${METRIC_LABEL[metric]} saved`, "success");
      onSaved();
    } catch (e) {
      logError("FeatureCard.save", e);
      show(errMsg(e) ?? "Could not save", "error");
      throw e;
    }
  }

  const off = !d.isActive;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{METRIC_LABEL[metric]}</CardTitle>
        <CardDescription>{METRIC_DESCRIPTION[metric]}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold">
          <Checkbox checked={d.isActive} onCheckedChange={(v) => patch({ isActive: v === true })} aria-label={`Enable ${METRIC_LABEL[metric]}`} />
          Enabled
        </label>

        {(thresholdLabel || showGrace) && (
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            {thresholdLabel && (
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">{thresholdLabel}</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="0.5" min="0"
                    value={d.thresholdValue}
                    disabled={off}
                    onChange={(e) => patch({ thresholdValue: e.target.value })}
                    className="h-8 w-20 text-right tabular-nums"
                  />
                  <span className="text-[12px] text-surface-500">hrs</span>
                </div>
              </label>
            )}
            {showGrace && (
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Grace</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="5" min="0" max="480"
                    value={d.graceMinutes}
                    disabled={off}
                    onChange={(e) => patch({ graceMinutes: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="h-8 w-20 text-right tabular-nums"
                  />
                  <span className="text-[12px] text-surface-500">min</span>
                </div>
              </label>
            )}
            <p className="pb-1 text-[12px] text-surface-500">
              {barSentence() ?? (off ? null : <span className="text-[var(--color-error)]">Enter hours above 0 and grace between 0 and 480 minutes.</span>)}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">
            Who approves or declines
          </span>
          <BoxRow fields={RECIPIENTS} draft={d} onChange={patch} disabled={off} />
          <p className="text-[12px] text-surface-500">
            Taken from the employee&rsquo;s own record. Sr Manager is whoever their reporting manager
            reports to &mdash; one step further up, not the HR Manager.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Who is emailed</span>
          <BoxRow fields={EMAILS} draft={d} onChange={patch} disabled={off} />
          <p className="text-[12px] text-surface-500">Chosen separately — someone can be emailed without being asked, or asked without being emailed.</p>
        </div>

        {WEEKLY(metric) && (
          <p className="text-[12px] text-surface-500">
            Runs every Monday for the week just finished, and adds the week to the manager&rsquo;s
            existing weekly-hours queue rather than a second one.
          </p>
        )}

        <div className="flex justify-end">
          <AsyncButton size="sm" onClick={() => save()} disabled={!dirty}>Save changes</AsyncButton>
        </div>
      </CardContent>
    </Card>
  );
}
